import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  LinearProgress,
  Chip,
  Paper
} from '@material-ui/core';
import {
  CheckCircle,
  Error,
  Warning,
  Settings,
  Palette,
  Language,
  Notifications,
  Security,
  VolumeUp
} from '@material-ui/icons';
import { toast } from 'react-toastify';

const SettingsTestPage = () => {
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState(false);

  const settingTests = [
    {
      id: 'theme',
      name: 'Theme Switching',
      description: 'Test light/dark/auto theme changes',
      icon: <Palette />,
      test: async () => {
        // Test theme switching
        const root = document.documentElement;
        
        // Test dark theme
        root.setAttribute('data-theme', 'dark');
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Test light theme
        root.setAttribute('data-theme', 'light');
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Test auto theme
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
        
        return { success: true, message: 'Theme switching works correctly' };
      }
    },
    {
      id: 'language',
      name: 'Language Setting',
      description: 'Test language preference storage',
      icon: <Language />,
      test: async () => {
        // Test language storage
        localStorage.setItem('app_language', 'en');
        const stored = localStorage.getItem('app_language');
        
        if (stored === 'en') {
          localStorage.setItem('app_language', 'ro'); // Reset to default
          return { success: true, message: 'Language setting stored successfully' };
        }
        
        return { success: false, message: 'Language setting storage failed' };
      }
    },
    {
      id: 'notifications',
      name: 'Notification Permissions',
      description: 'Test browser notification permissions',
      icon: <Notifications />,
      test: async () => {
        try {
          if ('Notification' in window) {
            if (Notification.permission === 'granted') {
              return { success: true, message: 'Notifications already enabled' };
            } else if (Notification.permission === 'denied') {
              return { success: false, message: 'Notifications are blocked' };
            } else {
              // Request permission
              const permission = await Notification.requestPermission();
              return { 
                success: permission === 'granted',
                message: permission === 'granted' ? 'Notification permission granted' : 'Notification permission denied'
              };
            }
          } else {
            return { success: false, message: 'Notifications not supported' };
          }
        } catch (error) {
          return { success: false, message: `Notification test failed: ${error.message}` };
        }
      }
    },
    {
      id: 'localStorage',
      name: 'Local Storage',
      description: 'Test settings persistence',
      icon: <Security />,
      test: async () => {
        try {
          const testData = { test: 'settings', timestamp: Date.now() };
          localStorage.setItem('settings_test', JSON.stringify(testData));
          
          const retrieved = JSON.parse(localStorage.getItem('settings_test'));
          localStorage.removeItem('settings_test');
          
          if (retrieved && retrieved.test === 'settings') {
            return { success: true, message: 'Local storage works correctly' };
          }
          
          return { success: false, message: 'Local storage test failed' };
        } catch (error) {
          return { success: false, message: `Local storage error: ${error.message}` };
        }
      }
    },
    {
      id: 'colorChange',
      name: 'Primary Color Change',
      description: 'Test dynamic color theme updates',
      icon: <Palette />,
      test: async () => {
        try {
          const originalColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--primary-color');
          
          // Test color change
          document.documentElement.style.setProperty('--primary-color', '#ff5722');
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Verify color was applied
          const appliedColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--primary-color').trim();
          
          // Reset color
          document.documentElement.style.setProperty('--primary-color', originalColor || '#904d71');
          
          if (!appliedColor || appliedColor === 'initial') {
            return { success: false, message: 'Color change was not applied' };
          }
          
          return { success: true, message: 'Color theming works correctly' };
        } catch (error) {
          return { success: false, message: `Color theming failed: ${error.message}` };
        }
      }
    },
    {
      id: 'mediaQuery',
      name: 'Auto Theme Detection',
      description: 'Test system theme preference detection',
      icon: <Settings />,
      test: async () => {
        try {
          const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
          const lightModeQuery = window.matchMedia('(prefers-color-scheme: light)');
          
          const supportsMediaQuery = darkModeQuery.media !== 'not all' || lightModeQuery.media !== 'not all';
          
          if (supportsMediaQuery) {
            return { 
              success: true, 
              message: `System theme: ${darkModeQuery.matches ? 'dark' : 'light'}` 
            };
          }
          
          return { success: false, message: 'Media query not supported' };
        } catch (error) {
          return { success: false, message: `Media query test failed: ${error.message}` };
        }
      }
    },
    {
      id: 'volume',
      name: 'Volume Control',
      description: 'Test audio volume setting storage',
      icon: <VolumeUp />,
      test: async () => {
        try {
          // Test volume setting
          const testVolume = 75;
          localStorage.setItem('volume_setting', testVolume.toString());
          
          const retrieved = parseInt(localStorage.getItem('volume_setting'));
          localStorage.removeItem('volume_setting');
          
          if (retrieved === testVolume) {
            return { success: true, message: 'Volume setting works correctly' };
          }
          
          return { success: false, message: 'Volume setting storage failed' };
        } catch (error) {
          return { success: false, message: `Volume test failed: ${error.message}` };
        }
      }
    }
  ];

  const runAllTests = async () => {
    setTesting(true);
    setTestResults({});
    
    toast.info('Rulează toate testele de setări...');
    
    for (const test of settingTests) {
      try {
        setTestResults(prev => ({ 
          ...prev, 
          [test.id]: { status: 'running', message: 'Testing...' } 
        }));
        
        const result = await test.test();
        
        setTestResults(prev => ({
          ...prev,
          [test.id]: {
            status: result.success ? 'success' : 'error',
            message: result.message
          }
        }));
        
        // Small delay between tests
        await new Promise(resolve => setTimeout(resolve, 300));
        
      } catch (error) {
        setTestResults(prev => ({
          ...prev,
          [test.id]: {
            status: 'error',
            message: `Test failed: ${error.message}`
          }
        }));
      }
    }
    
    setTesting(false);
    
    const successCount = Object.values(testResults).filter(r => r.status === 'success').length;
    const totalTests = settingTests.length;
    
    if (successCount === totalTests) {
      toast.success('Toate testele au trecut cu succes!');
    } else {
      toast.warning(`${successCount}/${totalTests} teste au trecut.`);
    }
  };

  const runSingleTest = async (test) => {
    setTestResults(prev => ({ 
      ...prev, 
      [test.id]: { status: 'running', message: 'Testing...' } 
    }));
    
    try {
      const result = await test.test();
      
      setTestResults(prev => ({
        ...prev,
        [test.id]: {
          status: result.success ? 'success' : 'error',
          message: result.message
        }
      }));
      
      if (result.success) {
        toast.success(`${test.name}: ${result.message}`);
      } else {
        toast.error(`${test.name}: ${result.message}`);
      }
      
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [test.id]: {
          status: 'error',
          message: `Test failed: ${error.message}`
        }
      }));
      
      toast.error(`${test.name}: Test failed`);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle style={{ color: '#4caf50' }} />;
      case 'error':
        return <Error style={{ color: '#f44336' }} />;
      case 'running':
        return <LinearProgress />;
      default:
        return <Warning style={{ color: '#ff9800' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'warning';
    }
  };

  return (
    <Container maxWidth="lg" style={{ padding: '24px' }}>
      <Box style={{ marginBottom: '32px', textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          🧪 Settings Implementation Test Suite
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Test all settings functionalities to ensure they work correctly
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={runAllTests}
          disabled={testing}
          style={{ marginTop: '16px' }}
        >
          {testing ? 'Running Tests...' : 'Run All Tests'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {settingTests.map((test) => {
          const result = testResults[test.id];
          
          return (
            <Grid item xs={12} md={6} lg={4} key={test.id}>
              <Card style={{ height: '100%', position: 'relative' }}>
                <CardContent>
                  <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    {test.icon}
                    <Typography variant="h6" style={{ marginLeft: '8px' }}>
                      {test.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {test.description}
                  </Typography>
                  
                  {result && (
                    <Box style={{ marginBottom: '16px' }}>
                      <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        {getStatusIcon(result.status)}
                        <Chip 
                          label={result.status.toUpperCase()} 
                          color={getStatusColor(result.status)}
                          size="small"
                          style={{ marginLeft: '8px' }}
                        />
                      </Box>
                      
                      <Paper 
                        elevation={1}
                        style={{ 
                          padding: '8px', 
                          backgroundColor: getStatusColor(result.status) === 'success' ? '#e8f5e8' : 
                                         getStatusColor(result.status) === 'error' ? '#ffebee' : '#fff3e0',
                          border: `1px solid ${getStatusColor(result.status) === 'success' ? '#4caf50' : 
                                              getStatusColor(result.status) === 'error' ? '#f44336' : '#ff9800'}`
                        }}
                      >
                        <Typography variant="body2">
                          {result.message}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => runSingleTest(test)}
                    disabled={testing || (result && result.status === 'running')}
                    fullWidth
                  >
                    {result && result.status === 'running' ? 'Testing...' : 'Test Individual'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      <Box style={{ marginTop: '32px', textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Test Summary
        </Typography>
        
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Chip 
              icon={<CheckCircle />}
              label={`${Object.values(testResults).filter(r => r.status === 'success').length} Passed`}
              color="primary"
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <Chip 
              icon={<Error />}
              label={`${Object.values(testResults).filter(r => r.status === 'error').length} Failed`}
              color="secondary"
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <Chip 
              icon={<Warning />}
              label={`${settingTests.length - Object.keys(testResults).length} Pending`}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default SettingsTestPage;
